export type RoleValue =
  | string
  | {
    name?: string;
    code?: string;
    role?: string;
    authority?: string;
  };

const AUTH_ENTRY_ROUTES = new Set([
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/auth/verify',
]);

const SUPER_ADMIN_ALIASES = new Set([
  'SUPERADMIN',
  'SUPER_ADMIN',
  'SYSADMIN',
  'SYS_ADMIN',
  'SYSTEMADMIN',
  'SYSTEM_ADMIN',
]);

const ROLE_COMPATIBILITY_MAP: Readonly<Record<string, readonly string[]>> = {
  SUPPLIER: ['SELLER'],
  PRODUCER: ['SELLER'],
  SELLER: ['SUPPLIER', 'PRODUCER'],
  DELIVERY_DRIVER: ['DELIVERY_PERSON'],
  DELIVERY_PERSON: ['DELIVERY_DRIVER'],
};

export function normalizeAuthRole(value: RoleValue | null | undefined): string {
  let role = '';

  if (typeof value === 'string') {
    role = value;
  } else if (value) {
    role = value.name ?? value.code ?? value.role ?? value.authority ?? '';
  }

  const normalized = role.trim().toUpperCase().replace(/^ROLE_/, '');
  return SUPER_ADMIN_ALIASES.has(normalized) ? 'SUPERADMIN' : normalized;
}

export function authRolesContain(userRoles: readonly string[], requestedRole: string): boolean {
  const normalizedRequestedRole = normalizeAuthRole(requestedRole);
  if (!normalizedRequestedRole) {
    return false;
  }

  if (userRoles.includes(normalizedRequestedRole)) {
    return true;
  }

  return (ROLE_COMPATIBILITY_MAP[normalizedRequestedRole] ?? [])
    .some((compatibleRole) => userRoles.includes(compatibleRole));
}

export function resolveRolePostLoginRedirect(
  userRoles: readonly string[],
  returnUrl?: string | null,
): string {
  const hasRole = (role: string) => authRolesContain(userRoles, role);
  const isSafeReturnUrl = !!returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//');

  if (hasRole('CONSUMER')) {
    return '/forbidden';
  }

  if (isSafeReturnUrl && !AUTH_ENTRY_ROUTES.has(returnUrl)) {
    return returnUrl;
  }

  if (hasRole('SUPERADMIN')) {
    return '/super-admin/dashboard';
  }

  if (hasRole('ADMIN')) {
    return '/admin/account-types';
  }

  if ([
    'SUPPLIER',
    'SELLER',
    'PRODUCER',
    'ORDER_MANAGER',
    'STOCK_MANAGER',
    'DELIVERY_DRIVER',
    'DELIVERY_PERSON',
  ].some(hasRole)) {
    return '/my-dashboard';
  }

  return '/dashboard/index';
}
